import { useEffect, useRef, type ReactNode } from "react"
import styled from "styled-components"
import { T } from "../adminTheme"
import { Muted, Row } from "./adminUi"

const Dialog = styled.dialog`
	border: 1px solid ${T.border}; border-radius: 16px; padding: 0; margin: max(24px, 7vh) auto auto; max-width: calc(100vw - 32px);
	max-height: calc(100dvh - max(48px, 14vh)); overflow: hidden; background: ${T.canvas}; color: ${T.text}; box-shadow: ${T.shadowLg};
	animation: bo-pop .26s ${T.ease} both;
	&::backdrop { background: rgba(17, 17, 22, .42); backdrop-filter: blur(3px); animation: bo-fade .2s ease both; }
`
const Close = styled.button`
	width: 32px; height: 32px; display: grid; place-items: center; border: 0; border-radius: 8px; background: none;
	color: ${T.muted}; cursor: pointer; transition: background-color .14s, color .14s;
	&:hover { background: ${T.hover}; color: ${T.text}; }
`

/**
 * The one modal every super-admin screen uses: title row with Close, content in Panels.
 * `label` is the accessible name (defaults to the title); Esc always closes.
 */
export const AdminModal = ({ title, label, subtitle, actions, width = 860, closeOnBackdrop = false, onClose, children }: {
	title: ReactNode; label?: string; subtitle?: ReactNode; actions?: ReactNode; width?: number; closeOnBackdrop?: boolean; onClose: () => void; children: ReactNode
}) => {
	const dialog = useRef<HTMLDialogElement>(null)
	useEffect(() => {
		const previousFocus = document.activeElement as HTMLElement | null
		const overflow = document.body.style.overflow
		const element = dialog.current
		if (element && !element.open) { if (typeof element.showModal === "function") element.showModal(); else element.setAttribute("open", "") }
		document.body.style.overflow = "hidden"
		return () => { document.body.style.overflow = overflow; previousFocus?.focus() }
	}, [])
	return <Dialog ref={dialog} aria-label={label ?? (typeof title === "string" ? title : undefined)} style={{ width }}
		onCancel={(event) => { event.preventDefault(); onClose() }}
		onClick={(event) => { if (closeOnBackdrop && event.target === event.currentTarget) onClose() }}>
		<div style={{ maxHeight: "calc(100dvh - max(48px, 14vh))", display: "flex", flexDirection: "column", minHeight: 0 }}>
			<header style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "18px 20px 16px", flex: "none", background: T.surface, borderBottom: `1px solid ${T.border}` }}>
				<div style={{ minWidth: 0 }}>
					<h2 style={{ margin: 0, fontSize: 18, lineHeight: 1.3, fontWeight: 650, letterSpacing: "-0.02em" }}>{title}</h2>
					{subtitle && <Muted style={{ display: "block", fontSize: 13, marginTop: 3, overflowWrap: "anywhere" }}>{subtitle}</Muted>}
				</div>
				<Row>{actions}<Close type='button' onClick={onClose} aria-label='Close' title='Close (Esc)'>
					<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round'><path d='M18 6 6 18M6 6l12 12' /></svg>
				</Close></Row>
			</header>
			<div style={{ overflowY: "auto", minHeight: 0, padding: 20, fontSize: 14, lineHeight: 1.5, display: "grid", gap: 16, alignContent: "start", gridAutoRows: "max-content" }}>{children}</div>
		</div>
	</Dialog>
}
